/**
 * In-memory archive upload progress for the create-project flow.
 * Survives route changes in the same tab. A refresh drops the byte counts
 * (the request dies with the page) but the bootstrap gate lock remains so
 * the project does not look ready.
 */
import { lockBootstrapGate } from './bootstrapGateLock';
import { randomId } from './randomId';

export type ArchiveUploadStatus = 'uploading' | 'failed';

export interface ArchiveUploadSnapshot {
  projectId: string;
  fileName: string;
  loaded: number;
  total: number;
  status: ArchiveUploadStatus;
  error?: string;
  /** 1-based position of the archive being sent, when several are uploaded. */
  fileIndex?: number;
  fileCount?: number;
  /** Archives of a multi-archive upload that did not upload, with their keys. */
  failedArchives?: FailedArchive[];
}

export interface FailedArchive {
  name: string;
  repositoryKey: string;
  error: string;
}

export type WorkspaceIntakeView = 'uploading' | 'upload-failed' | 'ingestion' | 'workspace';

const listeners = new Set<() => void>();
/** The File of each failed archive, by project and repository key (this tab only). */
const failedFiles = new Map<string, Map<string, File>>();
const byProject = new Map<string, ArchiveUploadSnapshot>();
const inFlight = new Set<string>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

export function subscribeArchiveUploads(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getArchiveUpload(projectId: string): ArchiveUploadSnapshot | undefined {
  return byProject.get(projectId);
}

/** Bytes actually accepted after a chunk that started at `offset`. */
export function bytesUploadedAfterChunk(offset: number, chunkSize: number, fileSize: number): number {
  if (fileSize <= 0) return 0;
  const sent = offset + Math.max(0, chunkSize);
  return Math.min(fileSize, Math.max(0, sent));
}

/**
 * Starts the single in-flight upload for a project and locks the ingestion gate.
 * Returns false when an upload for this project is already running.
 */
export function beginArchiveUpload(projectId: string, fileName: string, total: number): boolean {
  if (!projectId || inFlight.has(projectId)) return false;
  inFlight.add(projectId);
  lockBootstrapGate(projectId);
  byProject.set(projectId, {
    projectId,
    fileName,
    loaded: 0,
    total: Math.max(0, total),
    status: 'uploading',
  });
  emit();
  return true;
}

export function reportArchiveUploadBytes(projectId: string, loaded: number, total: number, fileName?: string): void {
  const current = byProject.get(projectId);
  if (!current || current.status !== 'uploading') return;
  const nextTotal = total >= 0 ? total : current.total;
  byProject.set(projectId, {
    ...current,
    fileName: fileName || current.fileName,
    loaded: Math.min(nextTotal, Math.max(0, loaded)),
    total: nextTotal,
  });
  emit();
}

export function setArchiveUploadFile(
  projectId: string,
  fileName: string,
  total: number,
  position?: { index: number; count: number },
): void {
  const current = byProject.get(projectId);
  if (!current || current.status !== 'uploading') return;
  byProject.set(projectId, {
    ...current,
    fileName,
    loaded: 0,
    total: Math.max(0, total),
    ...(position ? { fileIndex: position.index, fileCount: position.count } : {}),
  });
  emit();
}

/** Clears the byte snapshot. Leaves the bootstrap gate lock for the ingestion screen. */
export function finishArchiveUpload(projectId: string): void {
  inFlight.delete(projectId);
  if (!byProject.has(projectId)) return;
  byProject.delete(projectId);
  emit();
}

export function failArchiveUpload(projectId: string, error: string, failedArchives?: FailedArchive[]): void {
  inFlight.delete(projectId);
  const current = byProject.get(projectId);
  if (!current) return;
  byProject.set(projectId, { ...current, status: 'failed', error, ...(failedArchives ? { failedArchives } : {}) });
  emit();
}

export function workspaceIntakeView(
  upload: ArchiveUploadSnapshot | undefined,
  showIngestionGate: boolean,
): WorkspaceIntakeView {
  if (upload?.status === 'uploading') return 'uploading';
  if (upload?.status === 'failed') return 'upload-failed';
  if (showIngestionGate) return 'ingestion';
  return 'workspace';
}

export type ArchiveUploadClient = (
  projectId: string,
  file: File,
  options?: {
    onProgress?: (progress: { loaded: number; total: number; fileName: string }) => void;
    /** Identifies one archive of a multi-repository project, stable across retries. */
    repositoryKey?: string;
  },
) => Promise<unknown>;

function newRepositoryKey(): string {
  return randomId();
}

/**
 * The archive uploads for a new project. The returned promise is the upload
 * itself; callers can navigate as soon as this function returns (the lock and
 * snapshot are set synchronously).
 *
 * Several archives become several repositories of the one project: each is
 * sent with its own repository key, so none replaces another. A failed
 * archive does not stop the others; the upload then fails naming every
 * archive that did not upload, and the ones that did keep ingesting.
 */
export function startCreateArchiveUpload(
  projectId: string,
  files: File[],
  upload: ArchiveUploadClient,
): Promise<void> {
  const first = files[0];
  if (!first) return Promise.resolve();
  if (!beginArchiveUpload(projectId, first.name, first.size)) {
    return Promise.reject(new Error('An archive upload is already in progress for this project.'));
  }
  const multi = files.length > 1;
  return (async () => {
    if (!multi) {
      try {
        setArchiveUploadFile(projectId, first.name, first.size);
        await upload(projectId, first, {
          onProgress: ({ loaded, total, fileName }) => {
            reportArchiveUploadBytes(projectId, loaded, total, fileName);
          },
        });
        finishArchiveUpload(projectId);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        failArchiveUpload(projectId, message);
        throw err;
      }
      return;
    }
    // One key per archive, kept for every retry of that archive.
    await uploadKeyedArchives(
      projectId,
      files.map((file) => ({ file, repositoryKey: newRepositoryKey() })),
      upload,
    );
  })();
}

async function uploadKeyedArchives(
  projectId: string,
  archives: Array<{ file: File; repositoryKey: string }>,
  upload: ArchiveUploadClient,
): Promise<void> {
  const failed: FailedArchive[] = [];
  const kept = new Map<string, File>();
  for (const [index, { file, repositoryKey }] of archives.entries()) {
    setArchiveUploadFile(projectId, file.name, file.size, { index: index + 1, count: archives.length });
    try {
      await upload(projectId, file, {
        repositoryKey,
        onProgress: ({ loaded, total, fileName }) => {
          reportArchiveUploadBytes(projectId, loaded, total, fileName);
        },
      });
    } catch (err) {
      failed.push({ name: file.name, repositoryKey, error: err instanceof Error ? err.message : 'Upload failed' });
      kept.set(repositoryKey, file);
    }
  }
  if (failed.length > 0) {
    failedFiles.set(projectId, kept);
    const message = `${failed.length} of ${archives.length} archives did not upload (${failed
      .map((f) => `${f.name}: ${f.error}`)
      .join('; ')}).${
      failed.length < archives.length ? ' The others are being ingested as separate repositories.' : ''
    }`;
    failArchiveUpload(projectId, message, failed);
    throw new Error(message);
  }
  failedFiles.delete(projectId);
  finishArchiveUpload(projectId);
}

/**
 * Sends again the archives of a multi-archive upload that did not upload,
 * each with its original repository key, so each retry lands on the
 * repository it was meant to be and never creates a second one.
 */
export function retryFailedArchiveUploads(projectId: string, upload: ArchiveUploadClient): Promise<void> {
  const current = byProject.get(projectId);
  const files = failedFiles.get(projectId);
  const pending = (current?.failedArchives ?? [])
    .map((f) => ({ file: files?.get(f.repositoryKey), repositoryKey: f.repositoryKey }))
    .filter((a): a is { file: File; repositoryKey: string } => !!a.file);
  if (!current || current.status !== 'failed' || pending.length === 0) {
    return Promise.reject(new Error('There are no failed archives to retry in this tab.'));
  }
  inFlight.delete(projectId);
  if (!beginArchiveUpload(projectId, pending[0].file.name, pending[0].file.size)) {
    return Promise.reject(new Error('An archive upload is already in progress for this project.'));
  }
  return uploadKeyedArchives(projectId, pending, upload);
}

/** Leaves the archives that did not upload out of the project. */
export function dismissFailedArchiveUploads(projectId: string): void {
  failedFiles.delete(projectId);
  finishArchiveUpload(projectId);
}
